import { useEffect, useMemo, useState } from "react";
import { ChevronUp, ChevronDown, ChevronRight, ChevronLeft, Plus, Search, FileText, Settings, Calendar } from "lucide-react"
import dayjs, { Dayjs } from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import Layout from '../../Layout';

import EventModal from "./component/EventModal";
import './style.scss'

dayjs.extend(isoWeek);
dayjs.extend(isSameOrBefore);

/* ------------------------------------------------------------------ */
/*  Static labels only (not data) — safe to keep hardcoded             */
/* ------------------------------------------------------------------ */

const WEEKDAY_LABELS = ["Th2", "Th3", "Th4", "Th5", "Th6", "Th7", "CN"]; // Mon -> Sun
const MONTH_LABELS = [
  "Tháng 1", "Tháng 2", "Tháng 3", "Tháng 4", "Tháng 5", "Tháng 6",
  "Tháng 7", "Tháng 8", "Tháng 9", "Tháng 10", "Tháng 11", "Tháng 12",
];
const ROW_HEIGHT = 60; // px per hour — keep in sync with the h-[60px] rows below

// 24 hour labels (12:00 AM -> 11:00 PM), generated instead of typed out by hand
const HOURS = Array.from({ length: 24 }, (_, h) =>
  dayjs().hour(h).minute(0).format("hh:mm A")
);

/* ------------------------------------------------------------------ */
/*  Event data model                                                   */
/* ------------------------------------------------------------------ */

export interface CalendarEvent {
  id: string;
  title: string;
  start: string;        // ISO datetime string
  end?: string;          // ISO datetime string (defaults to start + 30min)
  allDay?: boolean;
  color?: string;         // tailwind classes for the dot / chip
  textColor?: string;
  done?: boolean;
  icon?: string;
}

// Fallback sample data so the calendar isn't empty when no `events` prop
// is passed. In real usage, fetch this from your API and pass it in as
// <WorkCalender events={events} /> — nothing about dates is hardcoded
// downstream, everything is computed relative to `today`.
const SAMPLE_EVENTS: CalendarEvent[] = [
  { id: "1", title: "Meeting dayli CRM THACO", start: dayjs().hour(9).minute(0).toISOString(), color: "bg-gray-300", done: true },
  { id: "2", title: "[CRM THACO AUTO]_Meeting Daily", start: dayjs().hour(9).minute(30).toISOString(), end: dayjs().hour(10).minute(30).toISOString(), color: "bg-gray-300" },
  { id: "3", title: "Meeting dự án", start: dayjs().add(1, "day").hour(14).minute(0).toISOString(), color: "bg-[#fef0c7] border border-[#fde047]", textColor: "text-[#b45309]" },
  { id: "4", title: "Meeting dự án", start: dayjs().add(4, "day").hour(14).minute(0).toISOString(), color: "bg-[#fef0c7] border border-[#fde047]", textColor: "text-[#b45309]" },
  { id: "5", title: "[Base - BEQ Holdings]", start: dayjs().date(4).toISOString(), allDay: true, icon: "ph-fill ph-check-circle", textColor: "text-[#1967d2]" },
  { id: "6", title: "Meeting dự án Stillprompt", start: dayjs().date(4).toISOString(), allDay: true, color: "bg-[#fca941]" },
  { id: "7", title: "Meeting dự án Stillprompt", start: dayjs().date(11).toISOString(), allDay: true, color: "bg-[#fca941]" },
  { id: "8", title: "Event Invite:Weekly Update", start: dayjs().date(7).toISOString(), allDay: true, color: "bg-[#93c5fd]" },
  { id: "9", title: "Event Invite:Weekly Update", start: dayjs().date(14).toISOString(), allDay: true, color: "bg-[#93c5fd]" },
];

/* ------------------------------------------------------------------ */
/*  Pure helpers – derive every grid/label from a reference date       */
/* ------------------------------------------------------------------ */

interface MonthCell {
  date: Dayjs;
  day: number;
  faded: boolean;
  today: boolean;
  events: CalendarEvent[];
}

function buildMonthGrid(monthDate: Dayjs, events: CalendarEvent[]): MonthCell[] {
  const startOfMonth = monthDate.startOf("month");
  const endOfMonth = monthDate.endOf("month");
  const gridStart = startOfMonth.startOf("isoWeek");
  const gridEnd = endOfMonth.endOf("isoWeek");

  const cells: MonthCell[] = [];
  let cursor = gridStart;
  while (cursor.isSameOrBefore(gridEnd, "day")) {
    cells.push({
      date: cursor,
      day: cursor.date(),
      faded: cursor.month() !== monthDate.month(),
      today: cursor.isSame(dayjs(), "day"),
      events: events.filter((e) => dayjs(e.start).isSame(cursor, "day")),
    });
    cursor = cursor.add(1, "day");
  }
  return cells;
}

function buildWeekHeader(anchorDate: Dayjs) {
  const weekStart = anchorDate.startOf("isoWeek");
  return Array.from({ length: 7 }, (_, i) => {
    const d = weekStart.add(i, "day");
    return {
      date: d,
      label: WEEKDAY_LABELS[i],
      dayNumber: d.date(),
      today: d.isSame(dayjs(), "day"),
    };
  });
}

function timeToTop(d: Dayjs) {
  return d.hour() * ROW_HEIGHT + (d.minute() / 60) * ROW_HEIGHT;
}

function eventsForDay(date: Dayjs, events: CalendarEvent[]) {
  return events
    .filter((e) => !e.allDay && dayjs(e.start).isSame(date, "day"))
    .map((e) => {
      const start = dayjs(e.start);
      const end = e.end ? dayjs(e.end) : start.add(30, "minute");
      const top = timeToTop(start);
      const height = Math.max(24, timeToTop(end) - top);
      return { ...e, top, height, timeLabel: `${start.format("h:mm")} - ${end.format("h:mmA")}` };
    });
}

function eventsForWeek(weekDays: { date: Dayjs }[], events: CalendarEvent[]) {
  return events
    .filter((e) => !e.allDay)
    .map((e) => {
      const start = dayjs(e.start);
      const dayIndex = weekDays.findIndex((wd) => wd.date.isSame(start, "day"));
      if (dayIndex === -1) return null;
      const end = e.end ? dayjs(e.end) : start.add(30, "minute");
      const top = timeToTop(start);
      const height = Math.max(24, timeToTop(end) - top);
      return { ...e, dayIndex, top, height };
    })
    .filter((e): e is CalendarEvent & { dayIndex: number; top: number; height: number } => e !== null);
}

/* ------------------------------------------------------------------ */
/*  Small presentational helpers                                       */
/* ------------------------------------------------------------------ */

function TimeColumn() {
  return (
    <div className="w-14 sm:w-16 shrink-0 border-r border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 z-10 flex flex-col">
      {HOURS.map((h) => (
        <div key={h} className="h-[60px] relative">
          <span className="absolute -top-2.5 right-2 text-[10px] text-gray-500 dark:text-gray-400 font-medium">
            {h}
          </span>
        </div>
      ))}
    </div>
  );
}

function HourGridLines() {
  return (
    <div className="absolute inset-0 pointer-events-none flex flex-col">
      {HOURS.map((_, i) => (
        <div key={i} className="h-[60px] border-b border-gray-100 dark:border-gray-800" />
      ))}
    </div>
  );
}

function CurrentTimeLine({ top }: { top: number }) {
  return (
    <div className="absolute left-0 right-0 z-20 flex items-center pointer-events-none" style={{ top }}>
      <div className="w-2.5 h-2.5 rounded-full bg-slate-600 dark:bg-slate-300 -ml-[5px]" />
      <div className="flex-1 h-[1.5px] bg-slate-600 dark:bg-slate-300" />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

type CalendarView = "day" | "week" | "month";

interface WorkCalenderProps {
  /** Pass real events from your API/store. Falls back to sample data. */
  events?: CalendarEvent[];
}

export default function WorkCalender({ events = SAMPLE_EVENTS }: WorkCalenderProps) {
  const [calendarView, setCalendarView] = useState<CalendarView>("week");
  const [viewMenuOpen, setViewMenuOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState<Dayjs>(() => dayjs()); // anchor for week/month nav
  const [selectedDay, setSelectedDay] = useState<Dayjs>(() => dayjs());
  const [eventModalOpen, setEventModalOpen] = useState(false);
  
  const [now, setNow] = useState(() => dayjs());

  // Keep the "now" line moving without a full page reload
  useEffect(() => {
    const id = setInterval(() => setNow(dayjs()), 60_000);
    return () => clearInterval(id);
  }, []);

  const monthGrid = useMemo(() => buildMonthGrid(currentDate, events), [currentDate, events]);
  const weekHeader = useMemo(() => buildWeekHeader(currentDate), [currentDate]);
  const weekEvents = useMemo(() => eventsForWeek(weekHeader, events), [weekHeader, events]);
  const dayEvents = useMemo(() => eventsForDay(selectedDay, events), [selectedDay, events]);
  const currentTimeTop = timeToTop(now);
  const isToday = (d: Dayjs) => d.isSame(now, "day");

  const openDay = (date: Dayjs) => {
    setSelectedDay(date);
    setCurrentDate(date);
    setCalendarView("day");
    setViewMenuOpen(false);
  };

  const goToday = () => {
    setCurrentDate(dayjs());
    setSelectedDay(dayjs());
  };

  const goPrev = () => {
    if (calendarView === "day") {
      const next = selectedDay.subtract(1, "day");
      setSelectedDay(next);
      setCurrentDate(next);
    } else if (calendarView === "week") {
      setCurrentDate((d) => d.subtract(1, "week"));
    } else {
      setCurrentDate((d) => d.subtract(1, "month"));
    }
  };

  const goNext = () => {
    if (calendarView === "day") {
      const next = selectedDay.add(1, "day");
      setSelectedDay(next);
      setCurrentDate(next);
    } else if (calendarView === "week") {
      setCurrentDate((d) => d.add(1, "week"));
    } else {
      setCurrentDate((d) => d.add(1, "month"));
    }
  };

  // Days shown before/after the real month, in the mini sidebar calendar
  const miniMonthStart = currentDate.startOf("month");
  const miniLeadingDays = (miniMonthStart.isoWeekday() - 1); // Mon = 1 -> 0 leading cells
  const miniLeading = Array.from({ length: miniLeadingDays }, (_, i) =>
    miniMonthStart.subtract(miniLeadingDays - i, "day")
  );
  const miniDaysInMonth = currentDate.daysInMonth();
  const miniCurrentMonthDays = Array.from({ length: miniDaysInMonth }, (_, i) =>
    miniMonthStart.add(i, "day")
  );
  const miniTrailingCount = (7 - ((miniLeading.length + miniCurrentMonthDays.length) % 7)) % 7;
  const miniTrailing = Array.from({ length: miniTrailingCount }, (_, i) =>
    miniMonthStart.endOf("month").add(i + 1, "day")
  );

  return (
    <Layout>
        <div className="flex h-screen overflow-hidden text-gray-800 dark:text-gray-100 font-sans bg-gray-100 dark:bg-gray-950">
          <main className="flex-1 flex flex-col h-full overflow-hidden bg-gray-50 dark:bg-gray-950">
            <div className="flex-1 overflow-auto bg-[#f3f4f6] dark:bg-gray-950">
              <section className="h-full flex w-full bg-white dark:bg-gray-900">
                {/* Left mini-calendar sidebar (hidden on mobile) */}
                <div className="w-[260px] border-r border-gray-200 dark:border-gray-800 hidden md:flex flex-col shrink-0 bg-white dark:bg-gray-900 z-10 shadow-[2px_0_10px_rgba(0,0,0,0.02)]">
                  <div className="p-4 pb-2">
                    <button
                      onClick={() => setEventModalOpen(true)}
                      className="flex items-center gap-2 bg-[#e6f4ea] dark:bg-emerald-900/40 hover:bg-[#dcefe3] dark:hover:bg-emerald-900/60 text-[#137333] dark:text-emerald-300 font-medium px-5 py-2.5 rounded-full shadow-sm transition-colors text-sm"
                    >
                      <Plus size={20}/>
                      Tạo sự kiện
                    </button>
                  </div>
                  <div className="px-5 pb-4 border-b border-gray-50 dark:border-gray-800">
                    <div className="flex items-center justify-between mb-4 mt-2">
                      <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100 tracking-tight">
                        {MONTH_LABELS[currentDate.month()]} {currentDate.year()}
                      </h3>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setCurrentDate((d) => d.subtract(1, "month"))}
                          className="text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 p-1.5 rounded-full transition-colors"
                        >
                          <ChevronLeft size={20}/>
                        </button>
                        <button
                          onClick={() => setCurrentDate((d) => d.add(1, "month"))}
                          className="text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 p-1.5 rounded-full transition-colors"
                        >
                          <ChevronRight size={20}/>
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-7 gap-y-3 gap-x-1 text-center text-xs">
                      {WEEKDAY_LABELS.map((d) => (
                        <div key={d} className="text-gray-500 dark:text-gray-400 font-semibold">{d}</div>
                      ))}
                      {miniLeading.map((d) => (
                        <div key={`pre-${d.format("YYYY-MM-DD")}`} className="text-gray-300 dark:text-gray-700 font-medium py-1">
                          {d.date()}
                        </div>
                      ))}
                      {miniCurrentMonthDays.map((d) => (
                        <div
                          key={d.format("YYYY-MM-DD")}
                          onClick={() => openDay(d)}
                          className={`font-medium rounded-full cursor-pointer py-1 transition-colors ${
                            d.isSame(selectedDay, "day")
                              ? "bg-[#e6f4ea] dark:bg-emerald-900/50 text-[#137333] dark:text-emerald-300 border border-[#16a34a] font-bold shadow-sm"
                              : "text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
                          }`}
                        >
                          {d.date()}
                        </div>
                      ))}
                      {miniTrailing.map((d) => (
                        <div key={`post-${d.format("YYYY-MM-DD")}`} className="text-gray-300 dark:text-gray-700 font-medium py-1">
                          {d.date()}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="px-4 flex-1 overflow-y-auto kt-scrollbar space-y-3 pt-4">
                    <div className="border border-green-400 dark:border-green-700 rounded-xl p-3 flex items-center justify-between cursor-pointer bg-white dark:bg-gray-900 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#ab47bc] text-white flex items-center justify-center text-xs font-bold shadow-sm">H</div>
                        <span className="text-sm font-bold text-gray-800 dark:text-gray-100">Lịch của tôi</span>
                      </div>
                      <ChevronUp size={20} className="text-gray-800 dark:text-gray-200 text-xs"/>
                    </div>
                    <div className="border border-gray-200 dark:border-gray-800 rounded-xl p-3 flex items-center justify-between cursor-pointer hover:border-gray-300 dark:hover:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-purple-50 dark:bg-purple-900/40 text-purple-600 dark:text-purple-300 flex items-center justify-center shadow-sm">
                          <Calendar size={20}/>
                        </div>
                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">Đồng bộ lịch</span>
                      </div>
                      <ChevronLeft size={20} className="ph-fill ph-caret-left text-gray-400 dark:text-gray-500 text-xs"/>
                    </div>
                  </div>
                  <div className="p-4 mt-auto border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900">
                    <div className="relative">
                      <Search size={20} className="absolute left-3 top-[25%] text-gray-500 dark:text-gray-400 text-lg"/>
                      <input
                        type="text"
                        className="w-full bg-gray-100 dark:bg-gray-800 border border-transparent rounded-lg pl-10 pr-4 py-2.5 text-sm text-gray-800 dark:text-gray-100 focus:bg-white dark:focus:bg-gray-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all placeholder-gray-500 dark:placeholder-gray-400 font-medium"
                        placeholder="Đối chiếu với đồng nghiệp"
                      />
                    </div>
                  </div>
                </div>
                {/* Main calendar area */}
                <div className="flex-1 flex flex-col min-w-0 bg-white dark:bg-gray-900">

                  {/* Toolbar */}
                  <div className="h-16 flex flex-wrap items-center justify-between gap-2 px-3 sm:px-6 border-b border-gray-200 dark:border-gray-800 shrink-0">
                    <div className="flex items-baseline gap-2 sm:gap-4">
                      <h1 className="text-lg sm:text-2xl font-bold text-gray-800 dark:text-gray-100 tracking-tight">
                        {calendarView === "day" && (
                          <>
                            {selectedDay.date()} <span className="text-[13px] sm:text-[15px] font-semibold ml-1">{MONTH_LABELS[selectedDay.month()]} {selectedDay.year()}</span>
                          </>
                        )}
                        {calendarView === "week" && (
                          <>
                            Tuần {currentDate.isoWeek()} <span className="text-[13px] sm:text-[15px] font-semibold ml-1">{MONTH_LABELS[currentDate.month()]}, {currentDate.year()}</span>
                          </>
                        )}
                        {calendarView === "month" && (
                          <>
                            {MONTH_LABELS[currentDate.month()]} <span className="text-[13px] sm:text-[15px] font-semibold ml-1">{currentDate.year()}</span>
                          </>
                        )}
                      </h1>
                      <div className="flex items-end gap-2 ml-2">
                        <button onClick={goPrev} className="text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors">
                          <ChevronLeft/>
                        </button>
                        <button onClick={goNext} className="text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors">
                          <ChevronRight/>
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3">
                      <button className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 border border-gray-300 dark:border-gray-700 rounded-md text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors shadow-sm bg-white dark:bg-gray-900">
                        <FileText size={20}/>
                        Yêu cầu phê duyệt
                      </button>
                      {/* View dropdown */}
                      <div className="relative inline-block text-left z-25">
                        <button
                          onClick={() => setViewMenuOpen((v) => !v)}
                          className="inline-flex items-center gap-2 px-3 py-1.5 border border-gray-300 dark:border-gray-700 rounded-md text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors shadow-sm bg-white dark:bg-gray-900 focus:outline-none"
                        >
                          <span>
                            {calendarView === "day" ? "Lịch ngày" : calendarView === "week" ? "Tuần" : "Tháng"}
                          </span>
                          <ChevronDown size={20}/>
                        </button>
                        {viewMenuOpen && (
                          <div className="origin-top-right absolute right-0 mt-2 w-44 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black/5 dark:ring-white/10 divide-y divide-gray-100 dark:divide-gray-700">
                            <div className="py-1">
                              {([
                                { key: "day", label: "Lịch ngày", icon: "ph ph-browser" },
                                { key: "week", label: "Lịch tuần", icon: "ph ph-columns" },
                                { key: "month", label: "Lịch tháng", icon: "ph ph-squares-four" },
                              ] as { key: CalendarView; label: string; icon: string }[]).map((opt) => (
                                <button
                                  key={opt.key}
                                  onClick={() => {
                                    setCalendarView(opt.key);
                                    setViewMenuOpen(false);
                                  }}
                                  className="w-full group flex items-center justify-between px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                                >
                                  <div className="flex items-center gap-3">
                                    <i className={`${opt.icon} text-lg text-gray-500 dark:text-gray-400`} />
                                    {opt.label}
                                  </div>
                                  {calendarView === opt.key && <i className="ph-bold ph-check text-green-500" />}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <button
                        onClick={goToday}
                        className="hidden sm:inline-flex items-center px-4 py-1.5 border border-gray-300 dark:border-gray-700 rounded-md text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors shadow-sm bg-white dark:bg-gray-900"
                      >
                        Hôm nay
                      </button>
                      <button className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors border border-gray-300 dark:border-gray-700 shadow-sm ml-1 bg-white dark:bg-gray-900">
                        <Settings size={20}/>
                      </button>
                    </div>
                  </div>

                  {/* Week view */}
                  {calendarView === "week" && (
                    <div className="flex-1 flex flex-col overflow-hidden bg-white dark:bg-gray-900">
                      <div className="grid grid-cols-[56px_1fr] border-b border-gray-200 dark:border-gray-800 shrink-0 bg-white dark:bg-gray-900">
                        <div className="border-r border-gray-100 dark:border-gray-800 flex items-end justify-center pb-2 text-[10px] font-medium text-gray-400 dark:text-gray-500">
                          GMT+07
                        </div>
                        <div className="grid grid-cols-7">
                          {weekHeader.map((d) => (
                            <div key={d.date.format("YYYY-MM-DD")} className="py-3 text-center border-r border-gray-100 dark:border-gray-800 last:border-r-0 flex flex-col items-center gap-1">
                              <span className={`text-[12px] font-semibold ${d.today ? "text-[#16a34a] dark:text-green-400" : "text-gray-500 dark:text-gray-400"}`}>{d.label}</span>
                              <div
                                onClick={() => openDay(d.date)}
                                className={`text-[20px] font-normal mt-1 w-9 h-9 flex items-center justify-center rounded-full cursor-pointer ${
                                  d.today ? "bg-[#dcfce7] dark:bg-emerald-900/50 text-[#16a34a] dark:text-emerald-300" : "text-gray-800 dark:text-gray-100"
                                }`}
                              >
                                {d.dayNumber}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="flex-1 overflow-y-auto">
                        <div className="h-[1440px] flex relative">
                          <TimeColumn />
                          <div className="flex-1 relative border-l border-gray-100 dark:border-gray-800">
                            {weekHeader.some((d) => d.today) && <CurrentTimeLine top={currentTimeTop} />}
                            <div className="absolute inset-0 grid grid-cols-7 pointer-events-none">
                              {Array.from({ length: 7 }).map((_, i) => (
                                <div key={i} className={i < 6 ? "border-r border-gray-100 dark:border-gray-800" : ""} />
                              ))}
                            </div>
                            <HourGridLines />
                            {weekEvents.map((e) => (
                              <div
                                key={e.id}
                                className={`absolute w-[calc(100%/7-4px)] rounded p-1 shadow-sm z-10 ${e.color ?? "bg-[#fef0c7] dark:bg-amber-900/50 border border-[#fde047] dark:border-amber-700"}`}
                                style={{ left: `calc(100%/7*${e.dayIndex} + 2px)`, top: e.top, height: e.height }}
                              >
                                <div className={`text-[11px] font-semibold truncate ${e.textColor ?? "text-[#b45309] dark:text-amber-300"}`}>{e.title}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Day view */}
                  {calendarView === "day" && (
                    <div className="flex-1 flex flex-col overflow-hidden bg-white dark:bg-gray-900">
                      <div className="border-b border-gray-200 dark:border-gray-800 shrink-0 bg-white dark:bg-gray-900">
                        <div className="py-2.5 text-center text-[13px] font-bold text-[#16a34a] dark:text-emerald-400">
                          <span className="text-gray-500 dark:text-gray-400 mr-2 font-semibold">Ngày</span> {selectedDay.date()}
                        </div>
                      </div>
                      <div className="flex-1 overflow-y-auto">
                        <div className="h-[1440px] flex relative">
                          <TimeColumn />
                          <div className="flex-1 relative border-l border-gray-100 dark:border-gray-800">
                            {isToday(selectedDay) && <CurrentTimeLine top={currentTimeTop} />}
                            <HourGridLines />
                            {dayEvents.map((e) => (
                              <div
                                key={e.id}
                                className="absolute left-2 right-4 bg-[#f8f9fa] dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-1.5"
                                style={{ top: e.top, height: e.height }}
                              >
                                <div className={`text-[11px] font-semibold truncate leading-tight ${e.done ? "text-gray-400 dark:text-gray-500 line-through" : "text-gray-700 dark:text-gray-200"}`}>{e.title}</div>
                                <div className="text-[10px] text-gray-400 dark:text-gray-500 font-medium leading-tight mt-0.5">{e.timeLabel}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Month view */}
                  {calendarView === "month" && (
                    <div className="flex-1 flex flex-col overflow-hidden bg-white dark:bg-gray-900">
                      <div className="grid grid-cols-7 border-b border-gray-200 dark:border-gray-800 shrink-0 bg-white dark:bg-gray-900">
                        {WEEKDAY_LABELS.map((d, i) => (
                          <div key={d} className={`py-2.5 text-center text-[13px] font-semibold text-gray-500 dark:text-gray-400 ${i < 6 ? "border-r border-gray-200 dark:border-gray-800" : ""}`}>
                            {d}
                          </div>
                        ))}
                      </div>
                      <div className="flex-1 grid grid-cols-7 auto-rows-[minmax(100px,1fr)] overflow-y-auto border-t border-l border-gray-200 dark:border-gray-800">
                        {monthGrid.map((cell) => (
                          <div
                            key={cell.date.format("YYYY-MM-DD")}
                            onClick={() => !cell.faded && openDay(cell.date)}
                            className={`p-1.5 border-b border-r border-gray-200 dark:border-gray-800 flex flex-col overflow-hidden ${
                              cell.faded ? "" : "cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                            } bg-white dark:bg-gray-900`}
                          >
                            {cell.today ? (
                              <div className="flex justify-center mb-1.5 mt-1">
                                <div className="w-6 h-6 rounded-full bg-[#16a34a] text-white flex items-center justify-center text-[13px] font-bold">{cell.day}</div>
                              </div>
                            ) : (
                              <div className={`text-center text-[13px] mb-1.5 mt-1 ${cell.faded ? "font-medium text-gray-400 dark:text-gray-600" : "font-bold text-gray-800 dark:text-gray-100"}`}>
                                {cell.day}
                              </div>
                            )}
                            {cell.events.length > 0 && (
                              <div className="flex-1 flex flex-col gap-0.5 kt-scrollbar">
                                {cell.events.map((ev) => (
                                  <div key={ev.id} className="flex items-start gap-1 px-1 py-0.5">
                                    {ev.icon ? (
                                      <i className={`${ev.icon} text-[#1967d2] dark:text-blue-400 text-[12px] shrink-0`} />
                                    ) : (
                                      <span className={`w-1.5 h-1.5 rounded-full mt-1 shrink-0 ${ev.color ?? "bg-gray-300"}`} />
                                    )}
                                    <span
                                      className={`text-[11px] leading-tight ${
                                        ev.done
                                          ? "font-medium text-gray-400 dark:text-gray-500 line-through"
                                          : ev.textColor
                                          ? `font-bold ${ev.textColor}`
                                          : "font-bold text-[#1967d2] dark:text-blue-400"
                                      }`}
                                    >
                                      {ev.title}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </section>
            </div>
          </main>
          {/* Event Modal */}
          {eventModalOpen && (
            <EventModal selectedDay={selectedDay} onClose={() => setEventModalOpen(false)}/>
          )}
        </div>
    </Layout>
  );
}